// Builds the list of words and phrases a learner can practice.
// Vocabulary and Pronunciation both use this, so a user never needs to save words by hand.
//
// Sources, in this order:
//   1. Words from lessons the learner has completed in the language they are learning
//   2. Words the learner saved in their word bank
//   3. A small starter set, only when the first two give too little to practice with

import { supabase } from '../supabase';
import { lessonsItalian } from './lessonsItalian';
import { lessonsEnglish } from './lessonsEnglish';

const LANGUAGE_CODES = { english: 'en', italian: 'it', spanish: 'es', french: 'fr', portuguese: 'pt' };

// Each starter item is written once in all five languages, so it works for all 25 pairings.
// The first twelve are single words, the rest are short phrases.
const STARTER_ITEMS = [
    { en: 'Hello', it: 'Ciao', es: 'Hola', fr: 'Bonjour', pt: 'Olá' },
    { en: 'Goodbye', it: 'Arrivederci', es: 'Adiós', fr: 'Au revoir', pt: 'Tchau' },
    { en: 'Please', it: 'Per favore', es: 'Por favor', fr: 'S’il vous plaît', pt: 'Por favor' },
    { en: 'Thank you', it: 'Grazie', es: 'Gracias', fr: 'Merci', pt: 'Obrigado' },
    { en: 'Excuse me', it: 'Mi scusi', es: 'Perdón', fr: 'Excusez-moi', pt: 'Com licença' },
    { en: 'Sorry', it: 'Mi dispiace', es: 'Lo siento', fr: 'Désolé', pt: 'Sinto muito' },
    { en: 'Water', it: 'Acqua', es: 'Agua', fr: 'Eau', pt: 'Água' },
    { en: 'Bread', it: 'Pane', es: 'Pan', fr: 'Pain', pt: 'Pão' },
    { en: 'Coffee', it: 'Caffè', es: 'Café', fr: 'Café', pt: 'Café' },
    { en: 'House', it: 'Casa', es: 'Casa', fr: 'Maison', pt: 'Casa' },
    { en: 'Friend', it: 'Amico', es: 'Amigo', fr: 'Ami', pt: 'Amigo' },
    { en: 'Family', it: 'Famiglia', es: 'Familia', fr: 'Famille', pt: 'Família' },
    { en: 'How are you?', it: 'Come stai?', es: '¿Cómo estás?', fr: 'Comment ça va ?', pt: 'Como você está?' },
    { en: 'My name is Anna.', it: 'Mi chiamo Anna.', es: 'Me llamo Anna.', fr: 'Je m’appelle Anna.', pt: 'Meu nome é Anna.' },
    { en: 'Where is the bathroom?', it: 'Dov’è il bagno?', es: '¿Dónde está el baño?', fr: 'Où sont les toilettes ?', pt: 'Onde fica o banheiro?' },
    { en: 'I don’t understand.', it: 'Non capisco.', es: 'No entiendo.', fr: 'Je ne comprends pas.', pt: 'Não entendo.' },
    { en: 'How much is it?', it: 'Quanto costa?', es: '¿Cuánto cuesta?', fr: 'Combien ça coûte ?', pt: 'Quanto custa?' },
    { en: 'Nice to meet you.', it: 'Piacere di conoscerti.', es: 'Mucho gusto.', fr: 'Enchanté.', pt: 'Prazer em conhecer você.' },
    { en: 'I would like a coffee, please.', it: 'Vorrei un caffè, per favore.', es: 'Quisiera un café, por favor.', fr: 'Je voudrais un café, s’il vous plaît.', pt: 'Eu queria um café, por favor.' },
    { en: 'Can you help me?', it: 'Puoi aiutarmi?', es: '¿Puedes ayudarme?', fr: 'Pouvez-vous m’aider ?', pt: 'Você pode me ajudar?' },
];

// Fewer than this many words from lessons and the word bank, and the starter set is added
const MIN_POOL_SIZE = 20;

function isPhrase(word) {
    return /\s/.test((word || '').trim());
}

function staticLessonFor(targetLang, interfaceLang, lessonSlug) {
    if (targetLang === 'italian') {
        const set = lessonsItalian[interfaceLang] || lessonsItalian.en;
        return set[lessonSlug] || null;
    }
    if (targetLang === 'english') {
        const set = lessonsEnglish[interfaceLang] || lessonsEnglish.en;
        return set[lessonSlug] || null;
    }
    return null;
}

function vocabularyFromLesson(lesson, topicSlug) {
    if (!lesson || !Array.isArray(lesson.slides)) return [];
    return lesson.slides
        .filter(slide => slide.type === 'vocabulary' && slide.word)
        .map(slide => ({
            word: String(slide.word).trim(),
            translation: slide.translation || '',
            phonetic: slide.phonetic || '',
            note: slide.note || '',
            emoji: slide.emoji || '',
            type: isPhrase(slide.word) ? 'phrase' : 'word',
            source: 'lesson',
            topicSlug: topicSlug || '',
        }));
}

export function getStarterItems(targetLang, interfaceLang) {
    const targetCode = LANGUAGE_CODES[targetLang] || 'it';
    const sameLanguage = targetCode === interfaceLang;
    return STARTER_ITEMS.map((item, index) => ({
        word: item[targetCode],
        // When the learner reads the app in the language they study, there is nothing to translate to
        translation: sameLanguage ? '' : (item[interfaceLang] || item.en),
        phonetic: '',
        note: '',
        emoji: '',
        type: index < 12 ? 'word' : 'phrase',
        source: 'starter',
        topicSlug: '',
    }));
}

export async function loadVocabularyPool(userId, targetLang, interfaceLang) {
    const items = [];

    // 1. Words from completed lessons
    const { data: completed } = await supabase
        .from('user_progress')
        .select('topic_slug, lesson_slug')
        .eq('user_id', userId)
        .eq('target_language', targetLang);

    const needFromCache = [];
    (completed || []).forEach(row => {
        const lesson = staticLessonFor(targetLang, interfaceLang, row.lesson_slug);
        if (lesson) {
            items.push(...vocabularyFromLesson(lesson, row.topic_slug));
        } else {
            needFromCache.push(row);
        }
    });

    if (needFromCache.length > 0) {
        const slugs = [...new Set(needFromCache.map(row => row.lesson_slug))];
        const { data: cached } = await supabase
            .from('lesson_cache')
            .select('topic_slug, lesson_slug, content')
            .eq('language', targetLang)
            .eq('interface_language', interfaceLang)
            .in('lesson_slug', slugs);
        const seen = new Set();
        (cached || []).forEach(row => {
            const key = `${row.topic_slug}__${row.lesson_slug}`;
            if (seen.has(key)) return;
            seen.add(key);
            items.push(...vocabularyFromLesson(row.content, row.topic_slug));
        });
    }

    // 2. The learner's word bank
    const { data: bank } = await supabase
        .from('word_bank')
        .select('word, translation, phonetic, note')
        .eq('user_id', userId)
        .eq('target_language', targetLang);

    (bank || []).forEach(row => {
        if (!row.word) return;
        items.push({
            word: String(row.word).trim(),
            translation: row.translation || '',
            phonetic: row.phonetic || '',
            note: row.note || '',
            emoji: '',
            type: isPhrase(row.word) ? 'phrase' : 'word',
            source: 'wordbank',
            topicSlug: '',
        });
    });

    // Remove duplicates, keeping the first copy
    const unique = [];
    const seenWords = new Set();
    items.forEach(item => {
        const key = item.word.toLowerCase();
        if (seenWords.has(key)) return;
        seenWords.add(key);
        unique.push(item);
    });

    // 3. Starter set, only when there is too little to practice with
    if (unique.length < MIN_POOL_SIZE) {
        getStarterItems(targetLang, interfaceLang).forEach(item => {
            const key = item.word.toLowerCase();
            if (seenWords.has(key)) return;
            seenWords.add(key);
            unique.push(item);
        });
    }

    return unique;
}

export function shuffle(list) {
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}